import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, FileDown, ChevronLeft, ChevronRight, CheckSquare, Square, Play, Download, AlertTriangle } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'
import { exportToXlsx } from '@/lib/exportXlsx'
import { ProviderReportsService, type IdName } from '@/services/modules/ProviderReportsService'
import { ReportTemplatesService, type ReportTemplate } from '@/services/modules/ReportTemplatesService'
import { AutomationReportsService, type AutomationReport } from '@/services/modules/AutomationReportsService'
import { useAuthority } from '@/hooks/useAuthority'

const GENERATE_KEY = 'provider-report/export'
const BUILTIN_LABELS: Record<string, string> = {
  medicar: 'Medicar (CSV)',
  'porto-seguro': 'Porto Seguro',
  auster: 'Auster',
}

const STEPS = ['Prestador', 'Tipos', 'Serviços', 'Produtos'] as const

export function ProvidersReportPage() {
  const [step, setStep] = useState(1)
  // Exportar no modelo enfileira em automation/reports, que exige outra
  // permissão além da tela (o Excel simples usa só a do relatório).
  const { can } = useAuthority()
  const canModel = can('commercial.reports.general')

  // dados
  const [providers, setProviders] = useState<IdName[]>([])
  const [templates, setTemplates] = useState<ReportTemplate[]>([])
  const [types, setTypes] = useState<IdName[]>([])
  const [services, setServices] = useState<IdName[]>([])
  const [products, setProducts] = useState<IdName[]>([])

  // seleções
  const [providerId, setProviderId] = useState('')
  const [selTypes, setSelTypes] = useState<Set<string>>(new Set())
  const [selServices, setSelServices] = useState<Set<string>>(new Set())
  const [selProducts, setSelProducts] = useState<Set<string>>(new Set())

  const [loading, setLoading] = useState(true) // já inicia carregando os prestadores
  const [generating, setGenerating] = useState(false)

  // Exportação no modelo (assíncrona, fila)
  const [modelReport, setModelReport] = useState<AutomationReport | null>(null)
  const [modelGenerating, setModelGenerating] = useState(false)
  const [chooseOpen, setChooseOpen] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => { if (timer.current) window.clearInterval(timer.current) }, [])

  useEffect(() => {
    Promise.all([
      ProviderReportsService.providers().catch(() => { toast.error('Erro ao carregar prestadores.'); return [] as IdName[] }),
      ReportTemplatesService.list({ size: 200, status: 'ATIVO' }).then(r => r.data).catch(() => [] as ReportTemplate[]),
    ]).then(([provs, tpls]) => { setProviders(provs); setTemplates(tpls) })
      .finally(() => setLoading(false))
  }, [])

  // Modelos vinculados ao prestador selecionado.
  const providerModels = useMemo(
    () => templates.filter(t => t.serviceProviders?.some(p => p.id === providerId)),
    [templates, providerId],
  )

  // Reset em cascata ao trocar o prestador.
  function changeProvider(id: string) {
    setProviderId(id)
    setSelTypes(new Set()); setSelServices(new Set()); setSelProducts(new Set())
    setTypes([]); setServices([]); setProducts([])
    setModelReport(null)
  }

  async function goNext() {
    if (step === 1) {
      if (!providerId) return toast.error('Selecione um prestador.')
      setLoading(true)
      try {
        const [t, s] = await Promise.all([
          ProviderReportsService.types(providerId),
          ProviderReportsService.services(providerId),
        ])
        setTypes(t); setServices(s)
        setStep(2)
      } catch { toast.error('Erro ao carregar tipos/serviços.') }
      finally { setLoading(false) }
      return
    }
    if (step === 2) {
      if (selTypes.size === 0) return toast.error('Selecione pelo menos um tipo.')
      setStep(3)
      return
    }
    if (step === 3) {
      if (selServices.size === 0) return toast.error('Selecione pelo menos um serviço.')
      setLoading(true)
      try {
        const p = await ProviderReportsService.products(providerId, [...selTypes], [...selServices])
        setProducts(p)
        setSelProducts(new Set())
        setStep(4)
      } catch { toast.error('Erro ao carregar produtos.') }
      finally { setLoading(false) }
      return
    }
  }

  function goBack() { if (step > 1) setStep(step - 1) }

  // Passo final: resolve o modelo do prestador (0 → Excel simples; 1 → esse
  // modelo; vários → modal). Em todos, os dados vêm da seleção do wizard.
  function handleExport() {
    if (selProducts.size === 0) return toast.error('Selecione pelo menos um produto.')
    if (providerModels.length === 0) { generateSimple(); return }
    if (providerModels.length === 1) { generateModel(providerModels[0], 'enterprise'); return }
    setChooseOpen(true)
  }

  // Exportação "de hoje" (Excel simples, client-side) — quando não há modelo.
  async function generateSimple() {
    setGenerating(true)
    try {
      const rows = await ProviderReportsService.beneficiaries({
        type_ids: [...selTypes],
        product_ids: [...selProducts],
      })
      if (rows.length === 0) {
        toast.error({ title: 'Nada a exportar', description: 'Nenhum beneficiário ativo para a seleção.' })
        return
      }
      const provider = providers.find((p) => p.id === providerId)?.name ?? 'prestador'
      exportToXlsx(`provedores-${provider}`, rows, [
        { header: 'Nome',       accessor: (r) => r.name },
        { header: 'CPF',        accessor: (r) => r.cpf },
        { header: 'Nascimento', accessor: (r) => r.birthDate },
        { header: 'Tipo',       accessor: (r) => r.type },
        { header: 'Plano',      accessor: (r) => r.plan },
        { header: 'Produto',    accessor: (r) => r.product },
        { header: 'Empresa',    accessor: (r) => r.company },
      ])
      toast.success({ title: 'Excel gerado', description: `${rows.length} beneficiário(s) exportado(s).` })
    } catch {
      toast.error('Erro ao gerar o Excel.')
    } finally {
      setGenerating(false)
    }
  }

  // Exportação no modelo (assíncrona) — usa a MESMA seleção do wizard.
  function pollModel(rid: string) {
    if (timer.current) window.clearInterval(timer.current)
    timer.current = window.setInterval(async () => {
      try {
        const fresh = await AutomationReportsService.show(rid)
        if (fresh.status === 'CONCLUIDO' || fresh.status === 'FALHOU') {
          window.clearInterval(timer.current); timer.current = undefined
          setModelGenerating(false); setModelReport(fresh)
          if (fresh.status === 'CONCLUIDO') toast.success('Relatório pronto.')
          else toast.error(fresh.errorMessage ?? 'Falha ao gerar relatório.')
        }
      } catch { /* tenta de novo na próxima iteração */ }
    }, 4000) as unknown as number
  }

  async function generateModel(model: ReportTemplate, variant: 'enterprise' | 'individual') {
    setChooseOpen(false); setModelGenerating(true); setModelReport(null)
    try {
      const metadata: Record<string, unknown> = {
        templateId: model.id,
        productIds: [...selProducts],
        typeIds: [...selTypes],
      }
      if (model.builtinKey === 'auster') metadata.variant = variant
      const r = await AutomationReportsService.create(GENERATE_KEY, metadata)
      pollModel(r.id)
      toast.success('Relatório enfileirado.')
    } catch (e) {
      setModelGenerating(false)
      toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao iniciar.')
    }
  }

  const providerOptions = useMemo(
    () => [{ value: '', label: 'Selecione o prestador' }, ...providers.map((p) => ({ value: p.id, label: p.name }))],
    [providers],
  )

  return (
    <>
      <PageHeader
        title="Relatório de Provedores"
        subtitle="Escolha o prestador e exporte no modelo vinculado a ele (ou no Excel simples)"
        crumbs={[{ label: 'Comercial' }, { label: 'Relatórios' }, { label: 'Provedores' }]}
      />

      <Stepper step={step} />

      <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6 mt-4 min-h-[280px]">
        {step === 1 && (
          <div className="max-w-md">
            <label className="block text-[12px] text-gray-600 mb-1.5">Prestador <span className="text-rose-500">*</span></label>
            <Select value={providerId} onChange={(e) => changeProvider(e.target.value)} options={providerOptions} searchable />
            <p className="text-xs text-gray-500 mt-2">Escolha o prestador para o qual o relatório será gerado.</p>
          </div>
        )}

        {step === 2 && (
          <CheckList
            title="Tipos (modalidades)" required loading={loading}
            items={types} selected={selTypes} onChange={setSelTypes}
            empty="Nenhum tipo disponível para este prestador."
          />
        )}

        {step === 3 && (
          <CheckList
            title="Serviços do prestador" required loading={loading}
            items={services} selected={selServices} onChange={setSelServices}
            empty="Nenhum serviço disponível para este prestador."
          />
        )}

        {step === 4 && (
          <div className="space-y-4">
            <CheckList
              title="Produtos" required loading={loading}
              items={products} selected={selProducts} onChange={setSelProducts}
              empty="Nenhum produto para a combinação de prestador, tipos e serviços."
            />
            <div className="border-t border-gray-100 pt-3 text-xs text-gray-600">
              {providerModels.length === 0 ? (
                <>Exporta no <strong>Excel simples</strong> (este prestador não tem modelo vinculado).</>
              ) : providerModels.length === 1 ? (
                <>Exporta no modelo <strong>{providerModels[0].name}</strong> ({providerModels[0].builtinKey ? (BUILTIN_LABELS[providerModels[0].builtinKey] ?? providerModels[0].builtinKey) : 'Personalizado'}).</>
              ) : (
                <>Este prestador tem <strong>{providerModels.length} modelos</strong> — ao gerar, você escolhe qual.</>
              )}
            </div>
            {(modelGenerating || modelReport) && (
              <div className="flex items-center gap-3">
                {modelGenerating && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500"><Loader2 size={14} className="animate-spin" /> Gerando na fila…</span>
                )}
                {modelReport?.status === 'CONCLUIDO' && modelReport.filePath && (
                  <a href={modelReport.filePath} target="_blank" rel="noopener noreferrer"
                    className="cursor-pointer inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition">
                    <Download size={14} /> Baixar arquivo
                  </a>
                )}
                {modelReport?.status === 'FALHOU' && (
                  <span className="inline-flex items-center gap-1 text-xs text-rose-600"><AlertTriangle size={14} /> Falha ao gerar</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navegação */}
      <div className="mt-4 flex items-center justify-between">
        <Button variant="secondary" onClick={goBack} disabled={step === 1 || loading} iconLeft={<ChevronLeft size={14} />}>
          Voltar
        </Button>
        {step < 4 ? (
          <Button onClick={goNext} loading={loading} iconLeft={<ChevronRight size={14} />}>
            Avançar
          </Button>
        ) : (
          <Button onClick={handleExport} loading={generating || modelGenerating} iconLeft={<FileDown size={14} />}
            disabled={providerModels.length > 0 && !canModel}
            title={providerModels.length > 0 && !canModel ? 'Sem permissão para gerar relatórios no modelo do prestador' : undefined}>
            {providerModels.length > 1 ? 'Escolher modelo e gerar' : 'Gerar'}
          </Button>
        )}
      </div>

      {/* Modal: prestador em vários modelos → escolher qual */}
      <Modal open={chooseOpen} onClose={() => setChooseOpen(false)} title="Em qual modelo exportar?">
        <div className="divide-y divide-gray-100">
          {providerModels.map((m) => (
            <ModelGenerateControl key={m.id} model={m} onGenerate={generateModel} disabled={modelGenerating} />
          ))}
        </div>
      </Modal>
    </>
  )
}

function Stepper({ step }: { step: number }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((label, i) => {
        const n = i + 1
        const active = n === step
        const done = n < step
        return (
          <div key={label} className="flex items-center gap-2">
            <div className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium',
              active ? 'bg-brand-500 text-white' : done ? 'bg-brand-50 text-brand-700' : 'bg-gray-100 text-gray-500',
            )}>
              <span className={cn(
                'w-5 h-5 rounded-full text-[11px] flex items-center justify-center',
                active ? 'bg-white/20' : done ? 'bg-brand-200 text-brand-800' : 'bg-gray-200',
              )}>{n}</span>
              {label}
            </div>
            {n < STEPS.length && <ChevronRight size={14} className="text-gray-300" />}
          </div>
        )
      })}
    </div>
  )
}

function CheckList({ title, required, items, selected, onChange, loading, empty }: {
  title: string
  required?: boolean
  items: IdName[]
  selected: Set<string>
  onChange: (s: Set<string>) => void
  loading?: boolean
  empty: string
}) {
  function toggle(id: string) {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id); else next.add(id)
    onChange(next)
  }
  const allOn = () => onChange(new Set(items.map((i) => i.id)))
  const allOff = () => onChange(new Set())

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-gray-700">
          {title}{required && <span className="text-rose-500"> *</span>}
          <span className="ml-2 text-xs font-normal text-gray-400">{selected.size} selecionado(s)</span>
        </h3>
        <div className="flex items-center gap-2">
          <button type="button" onClick={allOn} className="cursor-pointer inline-flex items-center gap-1 text-xs text-brand-600 hover:underline">
            <CheckSquare size={13} /> Marcar todos
          </button>
          <button type="button" onClick={allOff} className="cursor-pointer inline-flex items-center gap-1 text-xs text-gray-500 hover:underline">
            <Square size={13} /> Desmarcar todos
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">{empty}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[360px] overflow-y-auto p-1">
          {items.map((it) => {
            const on = selected.has(it.id)
            return (
              <label key={it.id} className={cn(
                'cursor-pointer flex items-center gap-2 rounded-md ring-1 px-3 py-2 text-sm transition',
                on ? 'ring-brand-300 bg-brand-50 text-brand-800' : 'ring-gray-200 text-gray-700 hover:bg-gray-50',
              )}>
                <input type="checkbox" checked={on} onChange={() => toggle(it.id)} className="w-4 h-4 accent-brand-500" />
                <span className="break-words" title={it.name}>{it.name}</span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Linha de modelo dentro da modal "Em qual modelo exportar?" (com variante Auster).
function ModelGenerateControl({ model, onGenerate, disabled }: {
  model: ReportTemplate
  onGenerate: (model: ReportTemplate, variant: 'enterprise' | 'individual') => void
  disabled?: boolean
}) {
  const [variant, setVariant] = useState<'enterprise' | 'individual'>('enterprise')
  const isAuster = model.builtinKey === 'auster'
  const typeLabel = model.builtinKey ? (BUILTIN_LABELS[model.builtinKey] ?? model.builtinKey) : 'Personalizado'

  return (
    <div className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-800 break-words">{model.name}</span>
          <Badge variant={model.builtinKey ? 'blue' : 'gray'}>{typeLabel}</Badge>
        </div>
      </div>
      {isAuster && (
        <Select
          value={variant}
          onChange={(e) => setVariant(e.target.value as 'enterprise' | 'individual')}
          options={[
            { value: 'enterprise', label: 'Empresarial' },
            { value: 'individual', label: 'Pessoa Física' },
          ]}
          searchable={false} clearable={false}
          wrapperClassName="w-44"
        />
      )}
      <Button size="sm" onClick={() => onGenerate(model, variant)} disabled={disabled} iconLeft={<Play size={14} />}>
        Gerar
      </Button>
    </div>
  )
}

export default ProvidersReportPage
